"use strict";

import angular from "angular";

/**
 * Projeção e Orçamento no escopo do grupo.
 *
 * Ambos são derivados de cenário — o que muda entre eles é o `type` do cenário (`projection` /
 * `budget`) —, então ficam junto de Cenários no menu, sob o mesmo divisor.
 *
 * Como em Demonstrações e Cenários do grupo, as telas são as mesmas da empresa recebendo
 * `groupData` no lugar de `CompanyData`.
 *
 * As quatro fontes existem aqui pelo mesmo motivo que existem na empresa: o grupo tem série
 * própria de Fiscal e de Pessoal (o consolidado contábil é que é derivado das empresas-membro).
 * Com só duas, os botões de Fiscal e Pessoal apareciam no seletor e não levavam a lugar nenhum.
 */
angular
    .module("nucont.accounting.group")
    .config(["$stateProvider", routeConfig]);

function routeConfig($stateProvider) {
    const lazyLoad = (chunk, moduleName) => (transition) => {
        const $ocLazyLoad = transition.injector().get("$ocLazyLoad");
        return chunk().then(() => $ocLazyLoad.inject(moduleName));
    };

    /**
     * O cabeçalho lê `data.layoutConfig` do estado de destino da transição. Sem isto, o grupo
     * abria Projeção e Orçamento sem título nenhum, enquanto a empresa — que declara o mesmo
     * bloco em cada estado-folha — mostrava normalmente.
     */
    const moduleTitle = (text, description) => ({
        layoutConfig: {
            model: "LayoutConfigModel5",
            ignore: { object: "headerConfig", path: "title" },
            setAttrs: [
                {
                    object: "headerConfig",
                    path: "moduleTitle.text",
                    value: text
                },
                {
                    object: "headerConfig",
                    path: "moduleTitle.description",
                    value: description
                },
                {
                    object: "headerConfig",
                    path: "moduleTitle.enabled",
                    value: true
                }
            ]
        }
    });

    const PROJECTION_TITLE = moduleTitle(
        "Projeção",
        "Projete cenários financeiros para apoiar suas decisões."
    );

    const BUDGET_TITLE = moduleTitle(
        "Orçamento",
        "Planeje receitas e despesas com orçamentos personalizados."
    );

    const sourceStates = (parentState, viewName, components, data) =>
        Object.entries(components).forEach(([stateName, config]) => {
            $stateProvider.state(`${parentState}.${stateName}`, {
                url: config.url,
                views: {
                    [`${viewName}@${parentState}`]: {
                        component: config.component,
                        bindings: { companyData: "groupData" }
                    }
                },
                params: { source: { value: config.source } },
                data,
                bodyClass: config.bodyClass
            });
        });

    $stateProvider.state("nucont.accounting.group.projection", {
        url: "/projecao?scenarioId&year&source&accountPlanType",
        params: {
            scenarioId: { value: null, dynamic: true },
            year: { value: null, dynamic: true },
            source: { value: null, dynamic: true },
            accountPlanType: { value: null, dynamic: true }
        },
        lazyLoad: lazyLoad(
            () =>
                import(
                    /* webpackChunkName: "feature-accounting-company-projection" */ "../../company/projection/projection.entry"
                ),
            "nucont.accounting.company.projection"
        ),
        views: {
            "admin@nucont.accounting": {
                component: "projectionViewComponent",
                bindings: { companyData: "groupData" }
            }
        },
        data: PROJECTION_TITLE,
        bodyClass: "projection-module"
    });

    sourceStates(
        "nucont.accounting.group.projection",
        "projection",
        {
            accounting: {
                url: "/contabil",
                component: "projectionAccountingViewComponent",
                source: "accounting",
                bodyClass: "projection-module"
            },
            fin: {
                url: "/financeiro",
                component: "projectionFinancialViewComponent",
                source: "fin",
                bodyClass: "projection-module"
            },
            tax: {
                url: "/fiscal",
                component: "projectionTaxViewComponent",
                source: "tax",
                bodyClass: "projection-module"
            },
            personnel: {
                url: "/pessoal",
                component: "projectionPersonnelViewComponent",
                source: "personnel",
                bodyClass: "projection-module"
            }
        },
        PROJECTION_TITLE
    );

    $stateProvider.state("nucont.accounting.group.budget", {
        url: "/orcamento?scenarioId&year&source&accountPlanType",
        params: {
            scenarioId: { value: null, dynamic: true },
            year: { value: null, dynamic: true },
            source: { value: null, dynamic: true },
            accountPlanType: { value: null, dynamic: true }
        },
        lazyLoad: lazyLoad(
            () =>
                import(
                    /* webpackChunkName: "feature-accounting-company-budget" */ "../../company/budget/budget.entry"
                ),
            "nucont.accounting.company.budget"
        ),
        views: {
            "admin@nucont.accounting": {
                component: "budgetViewComponent",
                bindings: { companyData: "groupData" }
            }
        },
        data: BUDGET_TITLE,
        bodyClass: "budget-module"
    });

    sourceStates(
        "nucont.accounting.group.budget",
        "budget",
        {
            accounting: {
                url: "/contabil",
                component: "budgetAccountingViewComponent",
                source: "accounting",
                bodyClass: "budget-module"
            },
            fin: {
                url: "/financeiro",
                component: "budgetFinancialViewComponent",
                source: "fin",
                bodyClass: "budget-module"
            },
            tax: {
                url: "/fiscal",
                component: "budgetTaxViewComponent",
                source: "tax",
                bodyClass: "budget-module"
            },
            personnel: {
                url: "/pessoal",
                component: "budgetPersonnelViewComponent",
                source: "personnel",
                bodyClass: "budget-module"
            }
        },
        BUDGET_TITLE
    );
}
